import React, { useState } from 'react';
import { Search, Edit2, Trash2 } from 'lucide-react';
import useAssessmentManagementStore from '@/stores/AdminStore/AssessmentManagementStore';
import './QuestionBank.css';

const QuestionBank: React.FC = () => {
  const { questions } = useAssessmentManagementStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [selectedType, setSelectedType] = useState('All Types');
  const [showAddModal, setShowAddModal] = useState(false);
  const [questionType, setQuestionType] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState<number | null>(null);

  const getTypeClass = (type: string) => {
    const typeMap: Record<string, string> = {
      'MCQ': 'admin-assessment-type-mcq',
      'T/F': 'admin-assessment-type-tf',
      'Essay': 'admin-assessment-type-essay',
      'Short': 'admin-assessment-type-short'
    };
    return typeMap[type] || '';
  };

  const getDifficultyClass = (difficulty: string) => {
    return `admin-assessment-difficulty-${difficulty}`;
  };

  return (
    <div className="admin-assessment-question-bank">
      <div className="admin-assessment-bank-header">
        <h3 className="admin-assessment-bank-title">Question Repository</h3>
        <button className="admin-assessment-add-question-btn" onClick={() => setShowAddModal(true)}>
          + Add Question
        </button>
      </div>

      <div className="admin-assessment-bank-filters">
        <div className="admin-assessment-search-wrapper">
          <Search size={18} className="admin-assessment-search-icon" />
          <input
            type="text"
            placeholder="Search Question"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-assessment-search-input"
          />
        </div>
        <select
          className="admin-assessment-filter-dropdown"
          value={selectedTopic}
          onChange={(e) => setSelectedTopic(e.target.value)}
        >
          <option>All Topics</option>
          <option>Leadership</option>
          <option>Programming</option>
          <option>Technical</option>
          <option>Project Management</option>
        </select>
        <select
          className="admin-assessment-filter-dropdown"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <option>All Types</option>
          <option>MCQ</option>
          <option>T/F</option>
          <option>Essay</option>
          <option>Short</option>
        </select>
      </div>

      <div className="admin-assessment-questions-list">
        {questions?.map((q) => (
          <div key={q.id} className="admin-assessment-question-item">
            <div className="admin-assessment-question-item-header">
              <div className="admin-assessment-question-badges">
                <span className={`admin-assessment-type-badge ${getTypeClass(q.type)}`}>{q.type}</span>
                <span className={`admin-assessment-difficulty-badge ${getDifficultyClass(q.difficulty)}`}>
                  {q.difficulty}
                </span>
                <span className="admin-assessment-topic-badge">{q.topic}</span>
                <span className="admin-assessment-points-badge">{q.points} pts</span>
              </div>
              <div className="admin-assessment-question-actions">
                <button className="admin-assessment-icon-btn admin-assessment-icon-btn-edit">
                  <Edit2 size={16} />
                </button>
                <button className="admin-assessment-icon-btn admin-assessment-icon-btn-delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <p className="admin-assessment-question-text">{q.question}</p>
            {q.options && (
              <div className="admin-assessment-question-options-grid">
                {q.options.map((option, idx) => (
                  <div
                    key={idx}
                    className={`admin-assessment-option-item ${option === q.correctAnswer ? 'admin-assessment-correct-answer' : ''}`}
                  >
                    {option}
                    {option === q.correctAnswer && (
                      <span className="admin-assessment-correct-indicator">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAddModal && (
        <div className="admin-assessment-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="admin-assessment-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-assessment-modal-header">
              <div className="admin-assessment-modal-title-wrapper">
                <span className="admin-assessment-modal-icon">?</span>
                <h3>Create New Question</h3>
              </div>
              <button className="admin-assessment-modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="admin-assessment-modal-body">
              <div className="admin-assessment-form-row">
                <div className="admin-assessment-form-field">
                  <label>Question Type</label>
                  <select
                    className="admin-assessment-form-select"
                    value={questionType}
                    onChange={(e) => setQuestionType(e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="MCQ">MCQ</option>
                    <option value="T/F">T/F</option>
                    <option value="Essay">Essay</option>
                    <option value="Short">Short</option>
                  </select>
                </div>
                <div className="admin-assessment-form-field">
                  <label>Topic</label>
                  <select className="admin-assessment-form-select">
                    <option>Select</option>
                    <option>Leadership</option>
                    <option>Programming</option>
                    <option>Technical</option>
                  </select>
                </div>
                <div className="admin-assessment-form-field">
                  <label>Difficulty</label>
                  <select className="admin-assessment-form-select">
                    <option>Select</option>
                    <option>Easy</option>
                    <option>Medium</option>
                    <option>Hard</option>
                  </select>
                </div>
              </div>

              <div className="admin-assessment-toggle-field">
                <span>Make Question mandatory?</span>
                <label className="admin-assessment-toggle">
                  <input type="checkbox" />
                  <span className="admin-assessment-toggle-slider"></span>
                </label>
              </div>

              <div className="admin-assessment-form-field">
                <label>Question</label>
                <textarea
                  className="admin-assessment-form-textarea"
                  placeholder="Enter your question..."
                  rows={3}
                />
              </div>

              {(questionType === 'MCQ' || questionType === 'T/F') && (
                <div className="admin-assessment-form-field">
                  <label>Options (Click radio button to mark correct answer)</label>
                  <div className="admin-assessment-options-list">
                    {options.map((option, idx) => (
                      <div key={idx} className="admin-assessment-option-input-row">
                        <input
                          type="radio"
                          name="correctAnswer"
                          checked={correctAnswerIndex === idx}
                          onChange={() => setCorrectAnswerIndex(idx)}
                          className="admin-assessment-radio-input"
                        />
                        <input
                          type="text"
                          placeholder={`Option ${idx + 1}`}
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...options];
                            newOptions[idx] = e.target.value;
                            setOptions(newOptions);
                          }}
                          className="admin-assessment-option-input"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="admin-assessment-form-field">
                <label>Points</label>
                <input type="number" className="admin-assessment-form-input" defaultValue={10} />
              </div>
            </div>
            <div className="admin-assessment-modal-footer">
              <button className="admin-assessment-btn-save">
                <span>💾</span> Save Question
              </button>
              <button className="admin-assessment-btn-cancel" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBank;
